import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

export function ExportDialog({
  open,
  onOpenChange,
  columns,
  selectedColumns,
  onToggleColumn,
  onSelectAll,
  onClearAll,
  pdfPageSize,
  onPdfPageSizeChange,
  onExportPDF,
  onExportExcel,
  title = 'Sütun Seçimi (PDF & Excel)',
  description = 'İndirilecek sütunları seçin',
  quickSelect,
  gridCols = 3,
  idPrefix = 'export',
  children,
}) {
  const gridClass = gridCols === 4
    ? 'grid grid-cols-2 md:grid-cols-4 gap-3 py-4'
    : gridCols === 2
      ? 'grid grid-cols-2 gap-3 py-4'
      : 'grid grid-cols-2 md:grid-cols-3 gap-3 py-4';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid={`${idPrefix}-dialog-title`}>{title}</DialogTitle>
          <DialogDescription className="text-gray-400">{description}</DialogDescription>
        </DialogHeader>

        <div className={`${gridClass} max-h-96 overflow-y-auto`} key={selectedColumns.join(',')}>
          {columns.map(col => {
            const colId = col.id || col.key;
            return (
              <div key={colId} className="flex items-center space-x-2">
                <Checkbox
                  id={`${idPrefix}_${colId}`}
                  checked={selectedColumns.includes(colId)}
                  onCheckedChange={() => onToggleColumn(colId)}
                  className="border-gray-600"
                  data-testid={`${idPrefix}-col-${colId}`}
                />
                <label htmlFor={`${idPrefix}_${colId}`} className="text-sm text-gray-300 cursor-pointer">
                  {col.label}
                </label>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-500">{selectedColumns.length} / {columns.length} sütun seçildi</p>

        {children}

        <DialogFooter className="flex flex-wrap gap-2 items-center">
          <Button
            data-testid={`${idPrefix}-select-all-btn`}
            onClick={onSelectAll}
            variant="outline"
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Tümünü Seç
          </Button>

          <Button
            data-testid={`${idPrefix}-clear-btn`}
            onClick={onClearAll}
            variant="outline"
            className="bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
          >
            Temizle
          </Button>

          {quickSelect && (
            <Button
              data-testid={`${idPrefix}-quick-select-btn`}
              onClick={quickSelect.onSelect}
              variant="outline"
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              {quickSelect.label}
            </Button>
          )}

          <Button
            data-testid={`${idPrefix}-cancel-btn`}
            onClick={() => onOpenChange(false)}
            variant="ghost"
            className="text-gray-400"
          >
            İptal
          </Button>

          {onPdfPageSizeChange && (
            <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
              <span className="text-xs text-gray-400 px-1">Kağıt:</span>
              <Button
                size="sm"
                onClick={() => onPdfPageSizeChange('a4')}
                data-testid={`${idPrefix}-a4-btn`}
                className={`h-7 px-3 text-xs ${pdfPageSize === 'a4' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}
              >A4</Button>
              <Button
                size="sm"
                onClick={() => onPdfPageSizeChange('a3')}
                data-testid={`${idPrefix}-a3-btn`}
                className={`h-7 px-3 text-xs ${pdfPageSize === 'a3' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}
              >A3</Button>
            </div>
          )}

          {onExportPDF && (
            <Button
              data-testid={`${idPrefix}-pdf-btn`}
              onClick={onExportPDF}
              disabled={selectedColumns.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              PDF{pdfPageSize ? ` (${pdfPageSize.toUpperCase()})` : ''}
            </Button>
          )}

          {onExportExcel && (
            <Button
              data-testid={`${idPrefix}-excel-btn`}
              onClick={onExportExcel}
              disabled={selectedColumns.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Excel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
